import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Smartphone } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Field, TextInput } from "@/components/auth/Field";
import { OtpInput } from "@/components/auth/OtpInput";
import { Spinner } from "@/components/common/FullScreenLoader";
import { homeFor, useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function MfaChallenge() {
  const navigate = useNavigate();
  const { user, applyAuth, signOut } = useAuth();
  const [mode, setMode] = useState("totp");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function verify(endpoint, payload) {
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, payload);
      applyAuth(data);
      navigate(homeFor(data.user), { replace: true });
    } catch (e) {
      const err = apiError(e);
      setError(err.code === "rate_limited" ? `Too many attempts. Wait ${err.retryAfter || 60}s and try again.` : err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    await signOut();
    navigate("/sign-in", { replace: true });
  }

  return (
    <AuthLayout
      eyebrow="Second factor"
      title={mode === "totp" ? "Enter your authenticator code." : "Use a recovery code."}
      subtitle={
        mode === "totp"
          ? `Open your authenticator app and enter the 6-digit code for ${user?.email || "your account"}.`
          : "Each recovery code works exactly once. Consider re-enrolling MFA afterwards."
      }
      footer={
        <button type="button" onClick={cancel} data-testid="mfa-cancel-button" className="transition-colors hover:text-electric">
          ← Cancel and sign out
        </button>
      }
    >
      {mode === "totp" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === 6) verify("/auth/mfa/verify", { code });
          }}
          className="space-y-6"
        >
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <Smartphone className="h-4 w-4 text-electric" strokeWidth={1.75} />
            Codes rotate every 30 seconds
          </div>
          <OtpInput value={code} onChange={setCode} onComplete={(value) => verify("/auth/mfa/verify", { code: value })} disabled={busy} testId="mfa-totp-input" />
          {error ? (
            <div role="alert" data-testid="mfa-error" className="bz-alert-error">
              {error}
            </div>
          ) : null}
          <button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-submit-button" className="bz-btn-primary w-full">
            {busy ? <Spinner /> : null}
            Verify
          </button>
          <button type="button" onClick={() => { setMode("recovery"); setError(null); }} data-testid="mfa-use-recovery-toggle" className="bz-btn-ghost w-full text-xs">
            <KeyRound className="h-3.5 w-3.5" strokeWidth={1.75} /> Use a recovery code instead
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify("/auth/mfa/recovery", { code: recovery.trim() });
          }}
          className="space-y-6"
        >
          <Field id="recovery" label="Recovery code">
            <TextInput
              id="recovery"
              autoComplete="off"
              placeholder="XXXXX-XXXXX"
              className="font-mono uppercase tracking-widest"
              value={recovery}
              onChange={(e) => setRecovery(e.target.value.toUpperCase())}
              data-testid="mfa-recovery-input"
            />
          </Field>
          {error ? (
            <div role="alert" data-testid="mfa-error" className="bz-alert-error">
              {error}
            </div>
          ) : null}
          <button type="submit" disabled={busy || recovery.replace(/[^A-Z0-9]/gi, "").length < 8} data-testid="mfa-recovery-submit-button" className="bz-btn-primary w-full">
            {busy ? <Spinner /> : null}
            Verify recovery code
          </button>
          <button type="button" onClick={() => { setMode("totp"); setError(null); }} data-testid="mfa-use-totp-toggle" className="bz-btn-ghost w-full text-xs">
            <Smartphone className="h-3.5 w-3.5" strokeWidth={1.75} /> Back to authenticator code
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
